import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { downloadData } from 'aws-amplify/storage';
import moment from 'moment';
import { DIRECTION_OPTIONS } from 'utils/constanst';

const downloadExcelFileFromS3 = async (templateType) => {
  // Number of retry attempts
  const maxRetries = 3;

  // Determine the template key based on the template type
  let templateKey = 'setting/template-vehicle.xlsx';
  if (templateType === 'pedestrian') {
    templateKey = 'setting/template-pedestrian.xlsx';
  }

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Download Excel template from S3
      const { body } = await downloadData({ key: templateKey }).result;
      const arrayBuffer = await body.arrayBuffer();
      return arrayBuffer;
    } catch (error) {
      if (attempt === maxRetries) {
        throw new Error(`Attempt ${attempt}/${maxRetries} failed: ${error.message}`);
      };
    }
  }
}

const insertImageToWorksheet = (workbook, worksheet, imgData, templateType) => {
  // Skip if no image data provided
  if (!imgData) {
    console.log('No image data provided, skipping image insertion');
    return;
  }

  try {
    // Validate base64 data
    if (typeof imgData !== 'string' || !imgData.includes('data:image/')) {
      console.warn('Invalid image data format');
      return;
    }

    // Extract base64 data (remove data:image/png;base64, prefix)
    const base64Data = imgData.split(',')[1];
    if (!base64Data || base64Data.length === 0) {
      console.warn('No base64 data found');
      return;
    }

    // Validate base64 string
    try {
      atob(base64Data); // Test if it's valid base64
    } catch (e) {
      console.warn('Invalid base64 encoding');
      return;
    }

    // Add image to workbook with proper configuration
    const imageId = workbook.addImage({
      base64: base64Data,
      extension: 'png',
    });

    let colIndex = 13; // Default column for vehicle template
    if (templateType === 'pedestrian') {
      colIndex = 12; // Column L for pedestrian template
    }
    // Insert image into worksheet at column M, row 2 with specific dimensions
    // Column M = index 12, N = index 13, Row 2 = index 1
    // Image size: 310px width, 170px height
    worksheet.addImage(imageId, {
      tl: { col: colIndex, row: 1 },
      ext: { width: 310, height: 170 }
    });

  } catch (error) {
    console.error('Failed to insert image:', error);
    console.warn('Continuing without image...');
  }
};

const fillDataIntoVehicleTemplate = (worksheet, datasets) => {
  // Vehicle data is filled in two columns sets: C-G for even index datasets, K-O for odd index datasets
  // Each dataset occupies 31 rows
  // Scenario name is filled at row 10, 41, 72, 103, 134
  // Hourly data is filled from row 14 -> 37, 45 -> 68, 76 -> 99 (24 hours)
  // Example: Dataset index 0 -> C10-G10 for name, C14-G37 for data
  //          Dataset index 1 -> K10-O10 for name, K14-O37 for data
  //          Dataset index 2 -> C41-G41 for name, C45-G68 for data
  //          Dataset index 3 -> K41-O41 for name, K45-O68 for data
  //          Dataset index 4 -> C72-G72 for name, C76-G99 for data
  //          Dataset index 5 -> K72-O72 for name, K76-O99 for data
  //          Dataset index 6 -> C103-G103 for name, C107-G130 for data
  //          Dataset index 7 -> K103-O103 for name, K107-O130 for data
  //          Dataset index 8 -> C134-G134 for name, C138-G161 for data
  //          Dataset index 9 -> K134-O134 for name, K138-O161 for data


  for (let index = 0; index < datasets.length; index++) {
    const dataset = datasets[index];

    // Determine vehicleCols based on dataset index (even or odd)
    let scenarioCol = 'C';
    let vehicleCols = ['C', 'D', 'E', 'F', 'G'];
    if (index % 2 !== 0) {
      vehicleCols = ['K', 'L', 'M', 'N', 'O'];
      scenarioCol = 'K';
    }
    // Fill scenario name
    const newIndex = Math.floor(index / 2);
    if (newIndex % 2 === 0) {
      worksheet.getCell(`${scenarioCol}${10 + (31 * newIndex)}`).value = dataset.name;
    } else {
      worksheet.getCell(`${scenarioCol}${10 + (31 * newIndex)}`).value = dataset.name;
    }

    // Fill hourly data
    for (let hour = 0; hour < 24; hour++) {
      const hourData = dataset.data[hour];
      worksheet.getCell(`${vehicleCols[0]}${14 + (31 * newIndex) + hour}`).value = hourData.car;
      worksheet.getCell(`${vehicleCols[1]}${14 + (31 * newIndex) + hour}`).value = hourData.bus;
      worksheet.getCell(`${vehicleCols[2]}${14 + (31 * newIndex) + hour}`).value = hourData.bike;
      worksheet.getCell(`${vehicleCols[3]}${14 + (31 * newIndex) + hour}`).value = hourData.truck;
      worksheet.getCell(`${vehicleCols[4]}${14 + (31 * newIndex) + hour}`).value = hourData.otherVehicle;
    }
  }
}

const fillDataIntoPedestrianTemplate = (worksheet, datasets) => {
  // Pedestrian data is filled in columns C, F, I, L, O for up to 5 datasets
  // Each dataset occupies 31 rows
  // Scenario name is filled at row 10 or 41 of each dataset block
  // Hourly data is filled from row 14 to row 37 or 45 to 68 (24 hours)
  // Example: Dataset index 0 -> C10 for name, C14-C37 for data
  //          Dataset index 1 -> F10 for name, F14-F37 for data
  //          Dataset index 2 -> I10 for name, I14-I37 for data
  //          Dataset index 3 -> L10 for name, L14-L37 for data
  //          Dataset index 4 -> O10 for name, O14-O37 for data
  //          Dataset index 5 -> C41 for name, C45-C68 for data
  //          Dataset index 6 -> F41 for name, F45-F68 for data
  //          Dataset index 7 -> I41 for name, I45-I68 for data
  //          Dataset index 8 -> L41 for name, L45-L68 for data
  //          Dataset index 9 -> O41 for name, O45-O68 for data


  const vehicleCols = ['C', 'F', 'I', 'L', 'O'];
  for (let index = 0; index < datasets.length; index++) {
    const dataset = datasets[index];
    if (index <=4) {
      // Fill scenario name
      worksheet.getCell(`${vehicleCols[index]}10`).value = dataset.name;
      // Fill hourly data
      for (let hour = 0; hour < 24; hour++) {
        const hourData = dataset.data[hour];
        worksheet.getCell(`${vehicleCols[index]}${14 + hour}`).value = hourData.pedestrian;
      }
    } else {
      // Handle for datasets beyond 5 (index 5 and above)
      const colIndex = index - 5;
      // Fill scenario name
      worksheet.getCell(`${vehicleCols[colIndex]}41`).value = dataset.name;
      // Fill hourly data
      for (let hour = 0; hour < 24; hour++) {
        const hourData = dataset.data[hour];
        worksheet.getCell(`${vehicleCols[colIndex]}${45 + hour}`).value = hourData.pedestrian;
      }
    }
  }
}

const fillDataIntoWorksheet = (templateType, worksheet, data) => {
  const sensorName = data.sensorName || 'No Information';
  const createAt = data.createAt || '';
  const datasets = data.datasets || [];
  // Fill sensor name and date
  worksheet.getCell('C4').value = sensorName;
  worksheet.getCell('C6').value = createAt;

  if (templateType === 'vehicle') {
    fillDataIntoVehicleTemplate(worksheet, datasets);
  } else {
    fillDataIntoPedestrianTemplate(worksheet, datasets);
  }
}

const downloadExcelWithSensorData = async (templateType, imgData, datasets, fileName) => {
  try {
    const arrayBuffer = await downloadExcelFileFromS3(templateType);
    // Check if arrayBuffer is valid
    if (arrayBuffer.byteLength === 0) throw new Error('Downloaded file is empty');

    // Verify it's an Excel file by checking the first few bytes
    const view = new Uint8Array(arrayBuffer);
    const isExcel = (view[0] === 0x50 && view[1] === 0x4B) || // ZIP signature (Excel 2007+)
                    (view[0] === 0xD0 && view[1] === 0xCF); // OLE signature (Excel 97-2003)
    if (!isExcel) throw new Error('Downloaded file is not a valid Excel format');

    // Load the Excel workbook
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);  

    // Get the first worksheet
    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      throw new Error('No worksheet found in the Excel file');
    }

    if (datasets && typeof datasets === 'object') {
      try {
        fillDataIntoWorksheet(templateType, worksheet, datasets);
      } catch (error) {
        throw new Error(`Failed to fill worksheet data: ${error.message}`);
      }
    }

    // Add image data to Excel if provided (non-blocking)
    try {
      insertImageToWorksheet(workbook, worksheet, imgData, templateType);
    } catch (imageError) {
      console.warn('Image insertion failed:', imageError.message);
      // Continue without image
    }

    // Generate and download the file with error handling
    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      saveAs(blob, fileName);
    } catch (writeError) {
      throw new Error(`Failed to generate Excel file: ${writeError.message}`);
    }
  } catch (error) {
    throw error;
  }
};

const retrieveDateTimeFromVideoKey = (videoKey) => {
  try {
    if (!videoKey) return '';
    const fileName = videoKey.split('/')[videoKey.split('/').length - 1];
    const timestamp = fileName.split('.')[0];
    
    // Convert timestamp to number and treat as milliseconds
    const timestampMs = parseInt(timestamp, 10);
    if (isNaN(timestampMs)) return '';
    
    return moment(timestampMs).add(1, 'minute').format('YYYY-MM-DD HH:mm');
  } catch (error) {
    console.error('Error retrieving date time from video key:', error);
    return '';
  }
}

const directionMapping = DIRECTION_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const directionDisplay = (direction) => {
  if (!direction) return '';

  if (['INBOUND', 'OUTBOUND'].includes(direction)) {
    return directionMapping[direction];
  }
  return `${directionMapping[direction]}方向`;
};

const retrieveLabelFromNameAndDirection = (targetSensor, cameraNumber) => {
  try {
    const name = targetSensor?.name || '';
    const direction1 = targetSensor?.data?.camera1Direction || '';
    const direction2 = targetSensor?.data?.camera2Direction || '';

    // Return label based on camera number
    if (cameraNumber === 1) {
      return `${name} ${directionDisplay(direction1)}`;
    } else
      return `${name} ${directionDisplay(direction2)}`;
  } catch (error) {
    console.error('Error retrieving label from name and direction:', error);
    return '';
  }
}

const retrieveListScenarioFromSensor = (sensor) => {
  if (!sensor) return [];
  const amountSensor = 10;
  const scenarios = [];
  for (let i=1; i<=amountSensor; i++) {
    if (sensor?.data?.[`trafficVolumeScenario${i}`] &&
      sensor?.data?.[`trafficVolumeLat${i}`] &&
      sensor?.data?.[`trafficVolumeLng${i}`] &&
      sensor?.data?.[`trafficVolumeType${i}`]) {
      scenarios.push({
        numberOrder: i,
        name: sensor?.data?.[`trafficVolumeName${i}`] || '',
        type: sensor?.data?.[`trafficVolumeType${i}`] || 'VEHICLE',
        lat: sensor?.data?.[`trafficVolumeLat${i}`],
        lng: sensor?.data?.[`trafficVolumeLng${i}`],
        rotation: sensor?.data?.[`trafficVolumeRotation${i}`],
        color: sensor?.data?.[`trafficVolumeArrowColor${i}`] || '#FF0000',
      });
    }
  }
  return scenarios;
}

export { downloadExcelWithSensorData, retrieveDateTimeFromVideoKey, retrieveLabelFromNameAndDirection, retrieveListScenarioFromSensor };